import axios from "axios";

const UNKNOWN_ERROR_MESSAGE = "En ukjent feil oppstod, vennligst prøv igjen senere eller ta kontakt med Bindeleddet";

export const bookingErrorMessage = (e: unknown): string => {
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof data === "object") {
    return "error" in data ? String((data as { error: unknown }).error) : Object.values(data).join("\n");
  }
  return UNKNOWN_ERROR_MESSAGE;
};

/**
 * The machine-readable error code from the backend, e.g. "date_taken" when someone else booked the date first.
 */
export const bookingErrorCode = (e: unknown): string | undefined => {
  const data = (e as { response?: { data?: { code?: unknown } } })?.response?.data;
  return typeof data?.code === "string" ? data.code : undefined;
};

export interface BookingRequestError {
  message: string;
  code?: string;
}

const formatDate = (date: Date) => date.toISOString().split("T")[0];

/**
 * Asks the backend whether the company can book, optionally on a given date, without booking.
 * Resolves to an error, or undefined if booking is possible.
 */
export const dryRunBooking = (date?: Date): Promise<BookingRequestError | undefined> =>
  axios
    .post("/booking-seasons/book/", { dry_run: true, date: date && formatDate(date) })
    .then(() => undefined)
    .catch(e => ({ message: bookingErrorMessage(e), code: bookingErrorCode(e) }));

/**
 * Fetches the booked days of the current season, used to keep the booking calendar up to date.
 */
export const getBookedDays = (): Promise<string[]> =>
  axios.get<{ booked_days: string[] }>("/booking-seasons/booked-days/").then(response => response.data.booked_days);

export const placeBookingRequest = (date: Date | undefined, comment: string | undefined) =>
  axios.post("/booking-seasons/book/", { date: date && formatDate(date), comment });

export type BookingProblemCode =
  | "not_open"
  | "booking_closed"
  | "not_company"
  | "not_approved"
  | "already_booked"
  | "no_main_contact"
  | "main_contact_no_email";

// Problems with the company's contact person. They are shown as hints, but only block the final booking step.
export const CONTACT_PROBLEMS: BookingProblemCode[] = ["no_main_contact", "main_contact_no_email"];

export interface BookingProblem {
  code: BookingProblemCode;
  message: string;
  // Set for "already_booked": the company's existing event this season
  event_id?: number;
}

export interface BookingEligibility {
  problems: BookingProblem[];
  // Estimated difference between the server clock and this device's clock, in milliseconds
  clockOffset: number;
}

/**
 * Fetches the reasons (if any) that would prevent the company from booking right now. Company-specific
 * problems are included also before booking opens. Also estimates how far this device's clock is off, using the
 * server time in the response and assuming the request took equally long each way.
 */
export const getBookingProblems = (): Promise<BookingEligibility> => {
  const sentAt = Date.now();
  return axios
    .get<{ problems: BookingProblem[]; server_time: string }>("/booking-seasons/booking-eligibility/")
    .then(response => {
      const receivedAt = Date.now();
      const serverTime = new Date(response.data.server_time).getTime();
      return { problems: response.data.problems, clockOffset: serverTime - (sentAt + receivedAt) / 2 };
    });
};
